import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { foldForSearch } from '@shared';

import * as fromGenerated from '../../_generated';
import {
  AdminGroupsActions,
  AdminGroupsSortColumn,
  AdminGroupsSortDirection,
} from './admin-groups.actions';

export const adminGroupsFeatureKey = 'adminGroups';

/** Server error surfaced inline in the form modal (vs. a toast). */
export const DUPLICATE_NAME_ERROR = 'duplicate-name';

export type AdminGroupsServerError = 'duplicate-name' | null;

export type AdminGroupsModalKind =
  | 'closed'
  | 'create'
  | 'edit'
  | 'delete-confirm'
  | 'edit-with-delete-confirm';

export interface AdminGroupsSort {
  column: AdminGroupsSortColumn | null;
  direction: AdminGroupsSortDirection;
}

export interface AdminGroupsModalState {
  kind: AdminGroupsModalKind;
  group?: fromGenerated.GroupView;
  members?: fromGenerated.IdAndDisplayNameView[];
  serverError?: AdminGroupsServerError;
}

export interface AdminGroupsState extends EntityState<fromGenerated.GroupView> {
  loaded: boolean;
  isLoading: boolean;
  isMutating: boolean;
  error: string | null;
  sort: AdminGroupsSort;
  filter: string;
  modal: AdminGroupsModalState;
  /** Lazily-loaded expand payloads for the count-cell dropdowns, by group id. */
  dropdownPayloads: Record<string, fromGenerated.GroupDropdownPayloadView>;
}

const entityAdapter: EntityAdapter<fromGenerated.GroupView> =
  createEntityAdapter<fromGenerated.GroupView>({
    selectId: (group) => group.id,
    sortComparer: false, // Ordering is owned by selectFilteredSortedGroups.
  });

export const initialAdminGroupsState: AdminGroupsState =
  entityAdapter.getInitialState({
    loaded: false,
    isLoading: false,
    isMutating: false,
    error: null,
    sort: { column: 'name', direction: 'asc' }, // spec: name ascending
    filter: '',
    modal: { kind: 'closed' },
    dropdownPayloads: {},
  });

const matchesFilter = (
  group: fromGenerated.GroupView,
  filter: string,
): boolean => {
  const needle = foldForSearch(filter.trim());
  if (!needle) {
    return true;
  }
  return foldForSearch(group.name ?? '').includes(needle);
};

const compareGroupsByColumn = (
  a: fromGenerated.GroupView,
  b: fromGenerated.GroupView,
  column: AdminGroupsSortColumn,
): number => {
  switch (column) {
    case 'name':
      return (a.name ?? '')
        .toLowerCase()
        .localeCompare((b.name ?? '').toLowerCase());
    case 'teammates':
      return (a.teammateCount ?? 0) - (b.teammateCount ?? 0);
    case 'skills':
      return (a.skillCount ?? 0) - (b.skillCount ?? 0);
    case 'members':
      return (a.memberCount ?? 0) - (b.memberCount ?? 0);
  }
};

export const adminGroupsFeature = createFeature({
  name: adminGroupsFeatureKey,
  reducer: createReducer(
    initialAdminGroupsState,

    on(AdminGroupsActions.loadGroups, (state) => ({
      ...state,
      isLoading: true,
      error: null,
    })),
    on(AdminGroupsActions.loadGroupsSuccess, (state, { groups }) =>
      entityAdapter.setAll(groups, {
        ...state,
        loaded: true,
        isLoading: false,
        error: null,
      }),
    ),
    on(AdminGroupsActions.loadGroupsFailure, (state, { error }) => ({
      ...state,
      error,
      isLoading: false,
    })),

    on(AdminGroupsActions.setSort, (state, { column, direction }) => ({
      ...state,
      sort: { column, direction },
    })),
    on(AdminGroupsActions.setFilter, (state, { filter }) => ({
      ...state,
      filter,
    })),

    on(AdminGroupsActions.openCreateModal, (state) => ({
      ...state,
      modal: { kind: 'create', serverError: null },
    })),
    on(AdminGroupsActions.openEditModal, (state, { group }) => ({
      ...state,
      // members is intentionally absent until loadGroupMembers$ resolves.
      modal: { kind: 'edit', group, serverError: null },
    })),
    on(AdminGroupsActions.openDeleteConfirm, (state, { group }) => ({
      ...state,
      modal: { kind: 'delete-confirm', group },
    })),
    on(AdminGroupsActions.openDeleteConfirmFromEdit, (state, { group }) => ({
      ...state,
      // Preserve the edit modal's staged members under the layered confirm.
      modal: { ...state.modal, kind: 'edit-with-delete-confirm', group },
    })),
    on(AdminGroupsActions.cancelDeleteConfirmReturnToEdit, (state) => ({
      ...state,
      modal: { ...state.modal, kind: 'edit' },
    })),
    on(AdminGroupsActions.closeModal, (state) => ({
      ...state,
      modal: { kind: 'closed' },
    })),

    on(AdminGroupsActions.loadGroupMembersSuccess, (state, { members }) => ({
      ...state,
      modal: { ...state.modal, members },
    })),

    on(
      AdminGroupsActions.loadDropdownPayloadSuccess,
      (state, { groupId, payload }) => ({
        ...state,
        dropdownPayloads: { ...state.dropdownPayloads, [groupId]: payload },
      }),
    ),

    on(AdminGroupsActions.createGroup, (state) => ({
      ...state,
      isMutating: true,
      modal: { ...state.modal, serverError: null },
    })),
    on(AdminGroupsActions.createGroupSuccess, (state, { group }) =>
      entityAdapter.upsertOne(group, {
        ...state,
        isMutating: false,
        modal: { kind: 'closed' },
      }),
    ),
    // Partial create: the group exists, so land the row and close the modal
    // exactly like a success — the difference is only the warning toast the
    // effect raises.
    on(AdminGroupsActions.createGroupMembersFailed, (state, { group }) =>
      entityAdapter.upsertOne(group, {
        ...state,
        isMutating: false,
        modal: { kind: 'closed' },
      }),
    ),
    on(AdminGroupsActions.createGroupFailure, (state, { error }) => ({
      ...state,
      isMutating: false,
      modal: {
        ...state.modal,
        serverError: error === DUPLICATE_NAME_ERROR ? 'duplicate-name' : null,
      },
    })),

    on(AdminGroupsActions.updateGroup, (state) => ({
      ...state,
      isMutating: true,
      modal: { ...state.modal, serverError: null },
    })),
    on(AdminGroupsActions.updateGroupSuccess, (state, { group }) =>
      entityAdapter.upsertOne(group, {
        ...state,
        isMutating: false,
        modal: { kind: 'closed' },
      }),
    ),
    on(AdminGroupsActions.updateGroupFailure, (state, { error }) => ({
      ...state,
      isMutating: false,
      modal: {
        ...state.modal,
        serverError: error === DUPLICATE_NAME_ERROR ? 'duplicate-name' : null,
      },
    })),

    on(AdminGroupsActions.deleteGroup, (state) => ({
      ...state,
      isMutating: true,
    })),
    on(AdminGroupsActions.deleteGroupSuccess, (state, { groupId }) =>
      entityAdapter.removeOne(groupId, {
        ...state,
        isMutating: false,
        modal: { kind: 'closed' },
      }),
    ),
    on(AdminGroupsActions.deleteGroupFailure, (state) => ({
      ...state,
      isMutating: false,
    })),
  ),
  extraSelectors: ({
    selectAdminGroupsState,
    selectSort,
    selectFilter,
    selectModal,
  }) => {
    const entitySelectors = entityAdapter.getSelectors(selectAdminGroupsState);

    const selectFilteredSortedGroups = createSelector(
      entitySelectors.selectAll,
      selectSort,
      selectFilter,
      (groups, sort, filter) => {
        // The seeded `Group(all)` is system-managed and hidden from the table.
        const userManaged = groups.filter((group) => group.type !== 'all');
        const filtered = filter
          ? userManaged.filter((group) => matchesFilter(group, filter))
          : userManaged;
        if (!sort.column) {
          return filtered;
        }
        const sign = sort.direction === 'asc' ? 1 : -1;
        const column = sort.column;
        return [...filtered].sort(
          (a, b) => sign * compareGroupsByColumn(a, b, column),
        );
      },
    );

    const selectServerError = createSelector(
      selectModal,
      (modal): AdminGroupsServerError => modal.serverError ?? null,
    );

    return {
      ...entitySelectors,
      selectFilteredSortedGroups,
      selectServerError,
    };
  },
});
